

<template>
    <section class="p-5 bg-echoes-background-black">
        <!-- Header -->
        <UsersHeader>
            <template #title>User Groups</template>
            <template #description>
                Manage and organize user access groups
            </template>
            <template #actions>
                <button @click="router.push('/backoffice/groups/save')" class="inline-flex items-center px-4 py-2.5 text-base font-medium rounded-xl bg-echoes-primary text-white hover:bg-echoes-primary/80 transition-colors duration-200">
                    <i class="mdi mdi-plus mr-2"></i>
                    Create Group
                </button>
            </template>
        </UsersHeader>

        <!-- Cards -->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard>
                <template #icon>
                    <i class="uil uil-users-alt text-2xl text-purple-500"></i>
                </template>
                <template #title>Total Groups</template>
                <template #value>{{ userGroups.length }}</template>
            </StatCard>
            <StatCard>
                <template #icon>
                    <i class="uil uil-clock text-2xl text-cyan-500"></i>
                </template>
                <template #title>Last Updated</template>
                <template #value>{{ formatDate(lastUpdated) }}</template>
            </StatCard>
            
            <StatCard>
                <template #icon>
                    <i class="uil uil-database-alt text-2xl text-blue-500"></i>
                </template>
                <template #title>Total Storage Used</template>
                <template #value>{{ totalStorageUsed }} GB</template>
            </StatCard>
        </div>

        <div class="py-6">
            <div class="bg-echoes-background-gray shadow-sm rounded-xl border border-echoes-border-light">
                <!-- Search and Filters -->
                <div class="p-4">
                    <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div class="w-full sm:w-96">
                            <TextInput 
                                v-model="searchQuery" 
                                placeholder="Search by username or email" 
                                class="w-full"
                            />
                        </div>
                        <div class="w-full sm:w-24">
                            <SelectInput
                                v-model="itemsPerPage"
                                :options="itemsPerPageOptions"
                                :hide-search="true"
                                @update:modelValue="currentPage = 1"
                            />
                        </div>
                    </div>
                </div>

                <!-- Table -->
                <div>
                    <table class="min-w-full divide-y divide-echoes-border-light">
                        <thead class="bg-echoes-border-light ">
                            <tr>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Name
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Access Code
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Storage Limit
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Created At
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Updated At
                                </th>
                                <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                            <template v-for="group in paginatedGroups" :key="group.id">
                                <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-200">
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div>
                                                <div class="text-sm font-medium text-white">
                                                    {{ group.name }}
                                                </div>
                                                <div class="text-sm text-white/80">
                                                    {{ group.description }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <span @click="copyAccessCode(group.id)" 
                                    class="px-2 py-1.5 w-[220px] text-xs font-medium rounded-full bg-echoes-primary/20 text-echoes-primary cursor-pointer hover:bg-echoes-primary/30 transition-colors duration-200 flex justify-center gap-1 border border-echoes-primary/30">
                                    {{ copiedStates[group.id] ? 'Copied!' : group.access_code }}
                                    <i :class="['mdi text-xs transition-colors duration-200', copiedStates[group.id] ? 'mdi-check text-green-500' : 'mdi-content-copy group-hover:text-echoes-primary']"></i>
                                </span>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="text-sm font-medium text-white">
                                            {{ group.storage_limit }} GB
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="text-sm font-medium text-white">
                                            {{ formatDate(group.created_at) }}
                                        </div>
                                        <div class="text-sm text-white/80">
                                           at {{ formatTime(group.created_at) }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="text-sm font-medium text-white">
                                            {{ formatDate(group.updated_at) }}
                                        </div>
                                        <div class="text-sm text-white/80">
                                            at {{ formatTime(group.updated_at) }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                <div class="flex justify-center space-x-3">
                                    <button @click="toggleDetails(group.id)"
                                        class="inline-flex items-center w-[150px] px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light">
                                        <svg 
                                            class="w-5 h-5 mr-2 text-white/80 transition-transform duration-200"
                                            :class="{ 'transform rotate-180': expandedRows.includes(group.id) }"
                                            xmlns="http://www.w3.org/2000/svg" 
                                            viewBox="0 0 20 20" 
                                            fill="currentColor"
                                        >
                                            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                        </svg>
                                        {{ expandedRows.includes(group.id) ? 'Hide Details' : 'Show Details' }}
                                    </button>
                                    <router-link :to="`/backoffice/groups/save/${group.id}`" 
                                        class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light">
                                        <i class="mdi mdi-pencil mr-2"></i>
                                        Edit
                                    </router-link>
                                </div>
                            </td>
                                </tr>
                                <tr v-if="expandedRows.includes(group.id)" class="bg-echoes-background-black border-b border-echoes-border-light">
                            <td colspan="6" class="px-6 py-4">
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <!-- Flags Section -->
                                    <div v-if="group.flagGroups && group.flagGroups.length > 0" class="bg-echoes-background-gray rounded-xl border border-echoes-border-light p-4">
                                        <h4 class="text-sm font-semibold text-white mb-4 flex items-center border-b border-echoes-border-light pb-2">
                                            <i class="mdi mdi-flag text-echoes-primary mr-2"></i>
                                            Flags ({{ group.flagGroups.length }})
                                        </h4>
                                        <div class="space-y-2">
                                            <div v-for="flagGroup in group.flagGroups" :key="flagGroup.id"
                                                    class="w-full px-4 py-2.5 text-sm rounded-xl bg-echoes-background-black">
                                                    <span class="text-sm font-medium text-white flex items-center">
                                                    <i class="mdi mdi-circle-medium text-echoes-primary mr-2"></i> 
                                                    {{ flagGroup.flag.name }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Permissions Section -->
                                    <div v-if="group.permissionsGroups && group.permissionsGroups.length > 0" class="bg-echoes-background-gray rounded-xl border border-echoes-border-light p-4">
                                        <h4 class="text-sm font-semibold text-white mb-4 flex items-center border-b border-echoes-border-light pb-2">
                                            <i class="mdi mdi-shield-check text-echoes-primary mr-2"></i>
                                            Permissions ({{ group.permissionsGroups.length }})
                                        </h4>
                                        <div class="space-y-2">
                                            <div v-for="permGroup in group.permissionsGroups" :key="permGroup.id"
                                                class="w-full px-4 py-2.5 text-sm rounded-xl bg-echoes-background-black">
                                                <div class="text-sm font-medium text-white flex items-center">
                                                    <i class="mdi mdi-circle-medium text-echoes-primary mr-2"></i> 
                                                    <span >{{ permGroup.permission.name }}</span>
                                                    <div class="border-l border-echoes-border-light h-4 mx-2"></div>
                                                    <span class="text-xs text-white/70">{{ permGroup.permission.description }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                            </template>
                        </tbody>
                    </table>
                    <!-- Pagination Footer -->
                    <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center text-sm text-white/80">
                                <span>Showing</span>
                                <span class="font-medium mx-1">
                                    {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                                </span>
                                <span>to</span>
                                <span class="font-medium mx-1">
                                    {{ Math.min(currentPage * itemsPerPage, filteredGroups.length) }}
                                </span>
                                <span>of</span>
                                <span class="font-medium ml-1">{{ filteredGroups.length }}</span>
                            </div>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="currentPage = Math.max(1, currentPage - 1)"
                                    :disabled="currentPage === 1"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <div class="flex items-center space-x-1">
                                    <template v-for="page in totalPages" :key="page">
                                        <button
                                            @click="currentPage = page"
                                            :class="[
                                                'px-3 py-1 rounded-lg text-sm font-medium',
                                                currentPage === page
                                                    ? 'bg-echoes-primary text-white'
                                                    : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                            ]"
                                        >
                                            {{ page }}
                                        </button>
                                    </template>
                                </div>
                                <button
                                    @click="currentPage = Math.min(totalPages, currentPage + 1)"
                                    :disabled="currentPage === totalPages"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
    <Toast />
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import axios from '@/plugins/axios';
import Toast from '@/components/toast.vue';
import { useToast } from '@/composables/use-toast';
import UsersHeader from '@/pages/admin/AdminHeader.vue';
import StatCard from '@/pages/admin/StatCard.vue';
import { useStorage } from '@/composables/use-storage';
import TextInput from '@/components/forms/inputs/text-input.vue';
import SelectInput from '@/components/forms/inputs/select-input.vue';

const router = useRouter();
const { hasPermission } = useStorage();

const { addToast } = useToast();

const userGroups = ref([]);
const users = ref([]);
const flags = ref([]);
const activeTab = ref();

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const itemsPerPageOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
];

const expandedRows = ref([]);
const copiedStates = ref({});

const tabs = computed(() => {
    const tabs = [];
    if (hasPermission('group-manager')) {
        tabs.push('groups');
    }
    if (hasPermission('group-set-users')) {
        tabs.push('users');
    }
    if (hasPermission('group-flags')) {
        tabs.push('flags');
    }
    return tabs;
});

const copyAccessCode = async (groupId) => {
    try {
        const group = userGroups.value.find(g => g.id === groupId);
        if (group) {
            await navigator.clipboard.writeText(group.access_code);
            copiedStates.value[groupId] = true;
            
            setTimeout(() => {
                copiedStates.value[groupId] = false;
            }, 2000);
        }
    } catch (err) {
        console.error('Failed to copy access code:', err);
        addToast('Failed to copy access code. Please try again.', 'error');
    }
};

// Format date
const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

// Format time
const formatTime = (time) => {
    return new Date(time).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    });
};


const toggleDetails = (id) => {
    const index = expandedRows.value.indexOf(id);
    if (index === -1) {
        expandedRows.value.push(id);
    } else {
        expandedRows.value.splice(index, 1);
    }
};

const filteredGroups = computed(() => {
    let filtered = userGroups.value;
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = userGroups.value.filter(group => 
            group.name.toLowerCase().includes(query)
        );
    }
    return filtered;
});

const totalPages = computed(() => {
    return Math.ceil(filteredGroups.value.length / itemsPerPage.value);
});

const totalStorageUsed = computed(() => {
    return userGroups.value.reduce((sum, group) => sum + Number(group.storage_limit || 0), 0);
});

const lastUpdated = computed(() => {
    const maxDate = userGroups.value.reduce((max, group) => {
        const updatedAt = new Date(group.updated_at);
        return updatedAt > max ? updatedAt : max;
    }, new Date(0));
    
    return maxDate.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
});

const paginatedGroups = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredGroups.value.slice(start, end);
});

const fetchUsers = async () => {
    try {
        if (hasPermission('group-set-users')) {
            const response = await axios.get('/api/groups');
            users.value = response.data;
        }
    } catch (error) {
        console.error('Error fetching users:', error);
        addToast('Failed to fetch users', 'error');
    }
};

const fetchGroups = async () => {
    try {
        if (hasPermission('group-manager') || hasPermission('group-set-users')) {
            const response = await axios.get('/api/groups');
            userGroups.value = response.data;
        }
    } catch (error) {
        console.error('Error fetching groups:', error);
        addToast('Failed to fetch groups', 'error');
    }
};

const fetchFlags = async () => {
    try {
        if (hasPermission('group-flags')) {
            const response = await axios.get('/api/flags');
            flags.value = response.data;
        }
    } catch (error) {
        console.error('Error fetching flags:', error);
        addToast('Failed to fetch flags', 'error');
    }
};

watch(activeTab, (newTab) => {
    if (newTab === 'users' && users.value.length === 0) {
        fetchGroups();
        fetchUsers();
    } else if (newTab === 'flags' && flags.value.length === 0) {
        fetchFlags();
    } else if (newTab === 'groups' && userGroups.value.length === 0) {
        fetchGroups();
    }

});

onMounted(() => {
    activeTab.value = tabs.value[0];
});
</script>

<style scoped lang="postcss">
.user-group__hero {
    @apply relative pt-16 pb-6 mb-6 border-b border-gray-200 dark:border-white/10 bg-gray-100;
    background-size: 100% 100%;
    background-position: 0px 0px;
    background-repeat: no-repeat;
}

.dark .user-group__hero {
    background: linear-gradient(to bottom, #1e293b, #0f172a, #020617);
}

.user-group__container {
    @apply relative overflow-hidden w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-20;
}
</style>